/**
 * "Masraflar ve Ödemeler" sekmeleri için dosyanın masraf kayıtlarını ayırır.
 * Ayrım, masraf girilirken seçilen Masraf Türü'ne göre yapılır; yeni bir
 * şey girilmez:
 *   gelen      → GELEN ÖDEME…, MÜVEKKİLDEN ALINAN… (gelir olarak işaretlenenler)
 *   muvekkile  → MÜVEKKİLE YAPILAN ÖDEME…
 *   masraf     → harç, tebligat, bilirkişi, noter… (kalan giderler)
 * BERİLE VERİLEN ve REFERANSA VERİLEN AVANS büro içi kayıttır; burada
 * gösterilmez (Masraflar bölümünde durur). Avukat hesapları müvekkile
 * yapılan ödemeleri Masraflar bölümünde de görmez; burada da görmez.
 * Eski yapıdaki ödeme sütunları (başvuru / tahkim aşaması) da satıra çevrilir.
 */

// Mac / iPhone'dan yüklenen dosya adlarında "ü", "ş" iki parça (u + ¨) gelir; önce
// birleştirilir, en sonda kalan işaretler atılır — iki yazılış da aynı sonucu verir.
const normalize = (s) => String(s || '')
  .normalize('NFC')
  .trim()
  .toLocaleUpperCase('tr-TR')
  .replace(/İ/g, 'I').replace(/Ü/g, 'U').replace(/Ö/g, 'O')
  .replace(/Ş/g, 'S').replace(/Ç/g, 'C').replace(/Ğ/g, 'G')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '');

const al = (m, ad) => m?.[ad] ?? m?.[ad.charAt(0).toLowerCase() + ad.slice(1)];

const sayi = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** Masraf türünün sekmesi; büro içi kayıtlar için null. */
export function odemeTuru(m) {
  const tur = normalize(al(m, 'MasrafTuru') || al(m, 'CustomMasrafTuru'));
  if (al(m, 'IsBerileVerilen') || tur === 'BERILE VERILEN' || tur.startsWith('REFERANSA VERILEN')) return null;
  if (tur.startsWith('MUVEKKILE YAPILAN ODEME')) return 'muvekkile';
  if (al(m, 'IsGelir') || tur.startsWith('GELEN ODEME') || tur.startsWith('MUVEKKILDEN ALINAN')) return 'gelen';
  return 'masraf';
}

/* Eski yapı: türü olmayan kayıtlarda ödemeler ayrı sütunlarda tutuluyordu. */
const ESKI_SUTUNLAR = [
  ['BasvuruAsamasindaGelenOdeme', 'gelen', 'Gelen ödeme (başvuru aşaması)'],
  ['TahkimAsamasindaGelenOdeme', 'gelen', 'Gelen ödeme (tahkim aşaması)'],
  ['MuvekkildenAlinanOdeme', 'gelen', 'Müvekkilden alınan ödeme'],
  ['MuvekkileSigortaAsamasindaYapilanOdeme', 'muvekkile', 'Müvekkile yapılan ödeme (sigorta aşaması)'],
  ['MuvekkileTahkimAsamasindaYapilanOdeme', 'muvekkile', 'Müvekkile yapılan ödeme (tahkim aşaması)'],
];

const tarihSirasi = (t) => {
  const z = t ? new Date(t).getTime() : NaN;
  return Number.isFinite(z) ? z : Number.MAX_SAFE_INTEGER;
};

/**
 * @param {Array} masraflar dosyanın masraf kayıtları
 * @param {{ avukat: boolean }} secenek avukat (yönetici olmayan) görünümü
 * @returns {{ gelen: Array, muvekkile: Array, masraf: Array }} her satır:
 *   { anahtar, tur, tutar, tarih, aciklama }
 */
export function odemeleriAyir(masraflar, { avukat = false } = {}) {
  const gruplar = { gelen: [], muvekkile: [], masraf: [] };
  for (const m of masraflar || []) {
    const id = al(m, 'Id');
    const aciklama = al(m, 'Aciklama') || '';
    const masrafTuru = al(m, 'MasrafTuru');
    if (masrafTuru) {
      const sekme = odemeTuru(m);
      if (sekme && !(avukat && sekme === 'muvekkile')) {
        gruplar[sekme].push({
          anahtar: `m-${id}`,
          tur: al(m, 'CustomMasrafTuru') || masrafTuru,
          tutar: sayi(al(m, 'Tutar')),
          tarih: al(m, 'SonTarih') || al(m, 'Tarih') || al(m, 'MasrafIstenmeTarihi') || al(m, 'CreatedDate') || null,
          aciklama,
        });
      }
    }
    // Kasa sayfasındaki kural: tutarı olan kayıt yeni yapıdır; eski sütunlara
    // yalnız tutarı olmayan (eski) kayıtlarda bakılır — aynı ödeme iki kez sayılmasın.
    if (al(m, 'Tutar') != null) continue;
    for (const [sutun, sekme, etiket] of ESKI_SUTUNLAR) {
      const tutar = sayi(al(m, sutun));
      if (!(tutar > 0) || (avukat && sekme === 'muvekkile')) continue;
      gruplar[sekme].push({
        anahtar: `m-${id}-${sutun}`,
        tur: etiket,
        tutar,
        tarih: al(m, `${sutun}Tarihi`) || al(m, 'CreatedDate') || null,
        aciklama,
      });
    }
  }
  for (const k of Object.keys(gruplar)) {
    gruplar[k].sort((a, b) => tarihSirasi(a.tarih) - tarihSirasi(b.tarih));
  }
  return gruplar;
}

export const toplamTutar = (satirlar) =>
  Math.round((satirlar || []).reduce((t, s) => t + (Number(s.tutar) || 0), 0) * 100) / 100;

/*
 * Belgeler'de kalmış masraf belgesi (bilirkişi ücreti, tahkim başvuru ücreti, harç,
 * tebligat gideri…) adından tanınır; kullanıcı onaylayınca Masraflar sekmesine taşınır.
 * Rapor, sözleşme, dilekçe, karar, tutanak ve serbest meslek makbuzu önerilmez; dava
 * kanıtı olan faturalar (onarım faturası gibi) de — yanlış yere taşınmasın.
 */
const MASRAF_IPUCLARI = [
  'BILIRKISI UCRET', 'BASVURU UCRET', 'KESIF UCRET', 'TEBLIGAT GIDER', 'TEBLIGAT UCRET',
  'NOTER UCRET', 'POSTA GIDER', 'POSTA UCRET', 'HARC', 'BARO PULU', 'MASRAF', 'GIDER AVANS',
];
const MASRAF_DEGIL = [
  'RAPOR', 'SOZLESME', 'VEKALET UCRET', 'AVUKATLIK UCRET', 'SERBEST MESLEK', 'DILEKCE', 'KARAR',
  'IHTARNAME', 'TUTANAK',
];

export function masrafBelgesiOlabilir(metin) {
  const t = normalize(metin).replace(/[_\-.,()]+/g, ' ').replace(/\s+/g, ' ');
  if (MASRAF_DEGIL.some((k) => t.includes(k))) return false;
  return MASRAF_IPUCLARI.some((k) => t.includes(k));
}

/*
 * Belgeler'de kalmış bir belgenin adından ait olduğu sekme:
 *   "müvekkile yapılan ödeme dekontu"       → 'muvekkile'
 *   "sigortadan gelen kısmi ödeme", tahsilat → 'gelen'
 *   "tahkim başvuru ücreti", harç, gider    → 'masraf'
 * Talep yazısı, dilekçe, karar, rapor, sözleşme, tutanak önerilmez. Yön belirtmeyen
 * "ödeme dekontu" da önerilmez (kime / kimden olduğu bilinmiyor). Kullanıcı onaylayınca taşınır.
 */
const ODEME_DEGIL = ['RAPOR', 'SOZLESME', 'DILEKCE', 'KARAR', 'IHTARNAME', 'TUTANAK', 'TALEP', 'YAZISI', 'SERBEST MESLEK'];
const ODEME_KELIMELERI = ['ODEME', 'DEKONT', 'HAVALE', 'EFT'];

export function odemeBelgesiSekmesi(metin) {
  const t = normalize(metin).replace(/[_\-.,()]+/g, ' ').replace(/\s+/g, ' ');
  if (ODEME_DEGIL.some((k) => t.includes(k))) return null;
  const odeme = ODEME_KELIMELERI.some((k) => t.includes(k));
  if (t.includes('MUVEKKILE') && odeme) return 'muvekkile';
  if ((t.includes('GELEN') && odeme) || t.includes('TAHSILAT')) return 'gelen';
  if (masrafBelgesiOlabilir(metin)) return 'masraf';
  return null;
}

/** Arama için: büyük/küçük harf, Türkçe harf ve Mac yazılışı farkı gözetmeden karşılaştırılabilir metin. */
export const aramaMetni = (s) => normalize(s);
